import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { NgIconsModule } from "@ng-icons/core";
import { EvaTooltipDirective } from "../eva-tooltip/eva-tooltip.directive";
import { isNgIconName } from "../utils/icon";

/**
 * EVA Button - Composant custom 100%
 *
 * Bouton entièrement personnalisé utilisant les tokens EVA (pas de PrimeNG).
 * Supporte variantes, sévérités, tailles et états.
 *
 * @example
 * // Bouton primaire standard
 * <eva-button label="Enregistrer" icon="pi pi-check" (onClick)="save()"></eva-button>
 *
 * // Bouton secondaire outlined
 * <eva-button label="Annuler" variant="outlined" severity="secondary"></eva-button>
 *
 * // Bouton danger avec confirmation
 * <eva-button label="Supprimer" icon="pi pi-trash" severity="danger"></eva-button>
 *
 * // Bouton icône rond
 * <eva-button icon="pi pi-pencil" [rounded]="true" tooltip="Modifier"></eva-button>
 */
@Component({
  selector: "eva-button",
  standalone: true,
  imports: [CommonModule, NgIconsModule, EvaTooltipDirective],
  templateUrl: "./eva-button.component.html",
  styleUrls: ["./eva-button.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EvaButtonComponent {
  // ==========================================================================
  // Inputs
  // ==========================================================================

  /** Texte du bouton */
  @Input() label: string = "";

  /** Classe d'icône PrimeNG (ex: "pi pi-check") */
  @Input() icon: string = "";

  /** Position de l'icône par rapport au label */
  @Input() iconPos: "left" | "right" = "left";

  /** Couleur de l'icône (override). Si null → hérite du bouton. Ex: "#eb0086" pour l'icône commentaire active. */
  @Input() iconColor: string | null = null;

  /** Variante de style du bouton */
  @Input() variant: "filled" | "soft" | "outlined" | "text" | "ghost" | "link" = "filled";

  /** Couleur/sévérité du bouton */
  @Input() severity: "primary" | "secondary" | "success" | "warning" | "danger" | "info" = "primary";

  /** Taille du bouton */
  @Input() size: "small" | "medium" | "large" = "medium";

  /** Désactive le bouton */
  @Input() disabled: boolean = false;

  /** Affiche un spinner de chargement */
  @Input() loading: boolean = false;

  /** Type HTML du bouton */
  @Input() type: "button" | "submit" | "reset" = "button";

  /** Bouton circulaire (pour icon-only) */
  @Input() rounded: boolean = false;

  /** Bouton pleine largeur */
  @HostBinding("class.eva-button--block") @Input() block: boolean = false;

  // ==========================================================================
  // Rétrocompatibilité (ancien wrapper PrimeNG)
  // ==========================================================================

  /**
   * @deprecated Utiliser `variant="outlined"` à la place
   */
  @Input() outlined: boolean = false;

  /**
   * @deprecated Utiliser `variant="text"` à la place
   */
  @Input() text: boolean = false;

  /** Badge compteur affiché en overlay (coin supérieur droit). null/0 = masqué */
  @Input() badge: string | number | null = null;

  /** Sévérité du badge */
  @Input() badgeSeverity: "primary" | "secondary" | "success" | "warning" | "danger" | "info" = "danger";

  /** Tooltip à afficher au survol */
  @Input() tooltip: string = "";

  /** Nom accessible du bouton (aria-label), à fournir pour un bouton icône seul */
  @Input() ariaLabel: string | null = null;

  /** Position du tooltip */
  @Input() tooltipPosition: "top" | "bottom" | "left" | "right" = "top";

  // ==========================================================================
  // Outputs
  // ==========================================================================

  /** Émis au clic sur le bouton (ignoré si désactivé ou en chargement) */
  @Output() onClick = new EventEmitter<MouseEvent>();

  // ==========================================================================
  // Computed Properties
  // ==========================================================================

  /**
   * Détermine si le bouton ne contient qu'une icône (sans label)
   */
  get isIconOnly(): boolean {
    return !!this.icon && !this.label;
  }

  /**
   * Détermine si l'icône utilise ng-icon (HeroIcons, Lucide, etc.)
   * ng-icons utilisent <ng-icon name="..."> au lieu de <i class="...">
   */
  get isHeroIcon(): boolean {
    return isNgIconName(this.icon);
  }

  /**
   * Variante calculée (avec rétrocompatibilité)
   * Les propriétés `outlined` et `text` ont priorité pour la rétrocompatibilité.
   */
  get computedVariant(): "filled" | "soft" | "outlined" | "text" | "ghost" | "link" {
    if (this.text) return "text";
    if (this.outlined) return "outlined";
    return this.variant;
  }

  /**
   * Tooltip calculé : si icon-only, utilise le tooltip fourni
   */
  get computedTooltip(): string {
    return this.tooltip;
  }

  // ==========================================================================
  // Methods
  // ==========================================================================

  handleClick(event: MouseEvent): void {
    if (!this.disabled && !this.loading) {
      this.onClick.emit(event);
    }
  }
}
